import loadable from '@loadable/component';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import DrawerListener from '@components/Drawer';
import IconButton from '@components/IconButton';
import AlertSettingDialogListener from '@components/Modal/Content/AlertSettingModalContent/FormDialog';
import RoundedButton from '@components/RoundedButton';
import SkeletonLoader from '@components/SkeletonLoader';

import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';

import { formatName } from '@utils/format';

import { useListAlertSettings } from 'generated/alert_setting.generated';

import AlertSettingsList from './AlertSettingsList';

const messages = defineMessages({
  newAlert: {
    id: 'clientProfileCard.alertSettings.newAlert',
    defaultMessage: 'New Alert',
  },
  alertSettings: {
    id: 'clientProfileCard.alertSettings.alertSettings',
    defaultMessage: 'Alert Settings',
  },
  description: {
    id: 'clientProfileCard.alertSettings.description',
    defaultMessage:
      '{patient} has {has_settings, select, true{the following} other{no}} alerts set up for their health metrics',
  },
  patient: {
    id: 'clientProfileCard.alertSettings.patient',
    defaultMessage: 'Patient',
  },
  title: {
    id: 'drawer.alertSettings.title',
    defaultMessage: 'Setup Alert Settings',
  },
});

export default function AlertSettingsCard() {
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();
  const {
    state: { activeProfile },
  } = useSession();
  const [vitall_patient_id, account_id = ''] = [
    activeProfile?.vitall_patient_id ?? '',
    activeProfile?.account_id,
  ];

  const { data, loading } = useListAlertSettings({
    variables: {
      input: {
        account_id,
        vitall_patient_id,
      },
    },
    skip: !account_id || !vitall_patient_id,
  });

  const AlertSettingDrawerContent = loadable(
    () => import('@components/Drawer/Content/Alert/AlertSettingDrawer'),
    { ssr: false }
  );

  return (
    <>
      <Stack
        alignItems="center"
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
      >
        <Stack spacing={0}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: 1,
            }}
          >
            <Typography color="primary.main" variant="h5">
              {formatMessage(messages.alertSettings)}
            </Typography>
            <DrawerListener
              content={<AlertSettingDrawerContent />}
              title={formatMessage(messages.title)}
            >
              {({ openDrawer }) => (
                <IconButton
                  color="info.main"
                  iconType="fal"
                  name="circle-info"
                  onClick={openDrawer}
                />
              )}
            </DrawerListener>
          </Box>

          <ContentLoader lineCount={1} loading={loading}>
            <Typography color="text.secondary" variant="body2">
              {formatMessage(messages.description, {
                patient: vitall_patient_id
                  ? formatName(
                      activeProfile?.first_name,
                      activeProfile?.last_name
                    )
                  : formatMessage(messages.patient),
                has_settings: (data?.listAlertSettings.length ?? 0) > 0,
              })}
            </Typography>
          </ContentLoader>
        </Stack>

        {flags.isAlertSettingUpdateAlertSettingEnabled && (
          <AlertSettingDialogListener>
            {({ handleOpen }) => (
              <RoundedButton
                autoSize
                height={20}
                onClick={handleOpen}
                iconName="plus"
                textTransform="capitalize"
              >
                {formatMessage(messages.newAlert)}
              </RoundedButton>
            )}
          </AlertSettingDialogListener>
        )}
      </Stack>

      <SkeletonLoader loading={loading} height={200}>
        <AlertSettingsList data={data?.listAlertSettings ?? []} />
      </SkeletonLoader>
    </>
  );
}
